// SESSION_COMPLETE — Section 10 + 13.
// Assembles the manifest, writes it (+ blobs) to pending_uploads, advances
// the participant's counters, and attempts an immediate upload.

import { useEffect, useState } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { useParticipantStore } from '../store/participantStore';
import { assembleManifest } from '../utils/manifest';
import { addPendingUpload, addSessionHistory } from '../db/indexedDB';
import { attemptUpload } from '../upload/uploadManager';
import { nextSessionTime, formatTime } from '../utils/schedule';

export default function SessionCompleteScreen({ onDone }) {
  const session = useSessionStore();
  const participant = useParticipantStore();
  const [phase, setPhase] = useState('saving'); // saving | uploading | done | failed

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const manifest = await assembleManifest(session.getSnapshot());
      const record = {
        session_id: session.sessionId,
        participant_id: session.participantId,
        created_at: new Date().toISOString(),
        status: 'pending',
        attempt_count: 0,
        last_attempt_at: null,
        manifest,
        text_audio_blob: session.textTrial?.blob,
        image_audio_blob: session.imageTrial?.blob ?? null,
      };
      await addPendingUpload(record);
      await addSessionHistory({
        session_id: session.sessionId,
        session_number: session.sessionNumber,
        content_index: session.contentIndex,
        start_ts: session.startTsUtc,
        end_ts: manifest.end_ts_utc,
        upload_status: 'pending',
        was_abandoned: false,
      });

      await participant.advanceAfterSession();

      if (cancelled) return;
      setPhase('uploading');
      const result = await attemptUpload(record);
      if (cancelled) return;
      setPhase(result.ok ? 'done' : 'failed');
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const next = nextSessionTime(participant.schedule);

  return (
    <div className="screen justify-center text-center">
      <h1 className="font-display text-3xl mb-3">Session complete!</h1>
      <p className="text-dim mb-8">Great work.</p>

      {phase === 'saving' && <p className="text-dim">Saving your session…</p>}
      {phase === 'uploading' && <p className="text-dim">Uploading…</p>}
      {phase === 'done' && <p className="text-ok">Data uploaded successfully.</p>}
      {phase === 'failed' && <p className="text-amber">Saved to your device. Will upload automatically.</p>}

      {next && (
        <p className="text-dim text-sm mt-6">
          Next reminder around {formatTime(next.date.getHours(), next.date.getMinutes())}
        </p>
      )}

      <button
        type="button"
        disabled={phase === 'saving'}
        onClick={() => {
          session.reset();
          onDone();
        }}
        className="btn-primary mt-10"
      >
        Back to Home
      </button>
    </div>
  );
}
